import { useCallback, useEffect, useState } from 'react';
import type { MatchHistoryItemDto } from '@redpandev/shared';
import { fetchHistory } from './historyClient';

/**
 * Historique paginé d'un joueur (spec 08 §3) : charge la première page dès
 * que le pseudo est connu, puis agrège les suivantes via `loadMore()` en
 * dédoublonnant par matchId (un curseur sur `finishedAt` peut recouvrir en
 * cas d'égalité de dates). `loadMore` renvoie le nombre de parties ajoutées
 * (pour l'annonce aria-live du bouton « Voir plus »).
 *
 * L'état est À CLÉ (le pseudo) : changer de profil invalide le résultat par
 * dérivation, sans reset dans un effet (règle react-hooks/set-state-in-effect).
 */
type HistoryState = {
  name: string;
  items: MatchHistoryItemDto[] | null;
  nextCursor: string | undefined;
  error: boolean;
};

export function useHistory(displayName: string | undefined) {
  const [state, setState] = useState<HistoryState | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Reset implicite : l'état ne vaut que pour le pseudo courant.
  const current = state?.name === displayName ? state : null;
  const items = current?.items ?? null;
  const nextCursor = current?.nextCursor;
  const error = current?.error ?? false;

  useEffect(() => {
    if (!displayName) return;
    let alive = true;
    fetchHistory(displayName)
      .then((page) => {
        if (!alive) return;
        setState({ name: displayName, items: page.items, nextCursor: page.nextCursor, error: false });
      })
      .catch(
        () =>
          alive && setState({ name: displayName, items: null, nextCursor: undefined, error: true }),
      );
    return () => {
      alive = false;
    };
  }, [displayName]);

  const loadMore = useCallback(async (): Promise<number> => {
    if (!displayName || !nextCursor || loadingMore) return 0;
    setLoadingMore(true);
    try {
      const page = await fetchHistory(displayName, { cursor: nextCursor });
      const seen = new Set((items ?? []).map((i) => i.matchId));
      const fresh = page.items.filter((i) => !seen.has(i.matchId));
      setState({
        name: displayName,
        items: [...(items ?? []), ...fresh],
        nextCursor: page.nextCursor,
        error: false,
      });
      return fresh.length;
    } catch {
      // Échec silencieux : le bouton reste, l'utilisateur peut réessayer.
      return 0;
    } finally {
      setLoadingMore(false);
    }
  }, [displayName, nextCursor, loadingMore, items]);

  return { items, error, hasMore: Boolean(nextCursor), loadingMore, loadMore };
}
